'use client';

import { useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    applyActionCode,
    sendEmailVerification,
    verifyPasswordResetCode,
    confirmPasswordReset,
} from 'firebase/auth';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { firebaseClientAuth } from '@/src/lib/firebase/clientApp';
import { Iconify } from 'src/components/iconify';
import { Form, Field } from 'src/components/hook-form';
import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------

type Status =
    | 'loading'
    | 'success'
    | 'already-verified'
    | 'reset-password'
    | 'reset-success'
    | 'error';

const ResetPasswordActionSchema = z
    .object({
        password: z
            .string()
            .min(1, { message: 'Lösenord är obligatoriskt' })
            .min(6, { message: 'Lösenordet måste innehålla minst 6 tecken' }),
        confirmPassword: z
            .string()
            .min(1, { message: 'Vänligen bekräfta lösenordet' }),
    })
    .refine((data) => data.password === data.confirmPassword, {
        message: 'Lösenorden matchar inte',
        path: ['confirmPassword'],
    });

type ResetPasswordActionSchemaType = z.infer<typeof ResetPasswordActionSchema>;

// ----------------------------------------------------------------------

export function ActionView() {
    const router = useRouter();
    const { checkUserSession } = useAuthContext();
    const searchParams = useSearchParams();

    const mode = searchParams.get('mode');
    const oobCode = searchParams.get('oobCode');

    const [status, setStatus] = useState<Status>('loading');
    const [errorMessage, setErrorMessage] = useState('');
    const [resetEmail, setResetEmail] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const methods = useForm<ResetPasswordActionSchemaType>({
        resolver: zodResolver(ResetPasswordActionSchema),
        defaultValues: {
            password: '',
            confirmPassword: '',
        },
    });

    const {
        handleSubmit,
        formState: { isSubmitting },
    } = methods;

    useEffect(() => {
        if (!oobCode) {
            if (mode === 'resetPassword') {
                setErrorMessage('Ingen återställningskod hittades i länken. Länken kan vara ofullständig eller ha gått ut.');
            } else if (mode === 'verifyEmail') {
                setErrorMessage('Ingen verifieringskod hittades i länken. Länken kan vara ofullständig.');
            } else {
                setErrorMessage('Länken saknar nödvändiga parametrar eller är ofullständig.');
            }
            setStatus('error');
            return;
        }

        if (mode === 'verifyEmail') {
            applyActionCode(firebaseClientAuth, oobCode)
                .then(async () => {
                    if (firebaseClientAuth.currentUser) {
                        await firebaseClientAuth.currentUser.reload();
                        await checkUserSession?.();
                    }
                    setStatus('success');

                    setTimeout(() => {
                        if (firebaseClientAuth.currentUser) {
                            router.push(paths.ONBOARDING);
                        } else {
                            router.push(`${paths.LOGGA_IN}?verified=true`);
                        }
                    }, 2000);
                })
                .catch(async (err: any) => {
                    if (firebaseClientAuth.currentUser) {
                        await firebaseClientAuth.currentUser.reload();
                        if (firebaseClientAuth.currentUser.emailVerified) {
                            setStatus('already-verified');
                            setTimeout(() => {
                                router.push(paths.ONBOARDING);
                            }, 2000);
                            return;
                        }
                    }

                    if (err.code === 'auth/invalid-action-code') {
                        setStatus('already-verified');
                    } else {
                        setErrorMessage(err.message || 'Ett okänt fel uppstod.');
                        setStatus('error');
                    }
                });
        } else if (mode === 'resetPassword') {
            verifyPasswordResetCode(firebaseClientAuth, oobCode)
                .then((email) => {
                    setResetEmail(email);
                    setStatus('reset-password');
                })
                .catch((err: any) => {
                    console.error(err);
                    if (err.code === 'auth/invalid-action-code' || err.code === 'auth/expired-action-code') {
                        setErrorMessage('Återställningslänken är ogiltig eller har redan gått ut. Begär en ny återställningslänk.');
                    } else {
                        setErrorMessage(err.message || 'Kunde inte verifiera återställningskoden.');
                    }
                    setStatus('error');
                });
        } else {
            setErrorMessage('Okänd åtgärdstyp.');
            setStatus('error');
        }
    }, [mode, oobCode, router, checkUserSession]);

    const handleResend = async () => {
        try {
            const user = firebaseClientAuth.currentUser;
            if (user) {
                const actionCodeSettings = {
                    url: `${window.location.origin}${paths.authAction}`,
                    handleCodeInApp: true,
                };
                await sendEmailVerification(user, actionCodeSettings);
                alert('Ett nytt verifieringsmail har skickats!');
            } else {
                router.push(paths.LOGGA_IN);
            }
        } catch (e: any) {
            alert('Kunde inte skicka mail: ' + e.message);
        }
    };

    const onResetPasswordSubmit = handleSubmit(async (data) => {
        if (!oobCode) return;
        try {
            await confirmPasswordReset(firebaseClientAuth, oobCode, data.password);
            setStatus('reset-success');
        } catch (err: any) {
            console.error(err);
            setErrorMessage(err.message || 'Kunde inte uppdatera lösenordet.');
            setStatus('error');
        }
    });

    const iconBox = (icon: string, color: string, bg: string) => (
        <Box
            sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
            }}
        >
            <Iconify icon={icon} width={44} sx={{ color }} />
        </Box>
    );

    return (
        <Container
            maxWidth="sm"
            sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center' }}
        >
            <Card sx={{ p: { xs: 4, sm: 6 }, width: 1, borderRadius: 3, textAlign: 'center' }}>
                <Typography variant="overline" color="text.disabled" sx={{ mb: 4, display: 'block' }}>
                    Flinkyen
                </Typography>

                {status === 'loading' && (
                    <>
                        <CircularProgress sx={{ mb: 3, color: 'primary.main' }} size={56} />
                        <Typography variant="h5" fontWeight={700} gutterBottom>
                            {mode === 'resetPassword' ? 'Verifierar återställningskod...' : 'Verifierar...'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Vänta ett ögonblick.
                        </Typography>
                    </>
                )}

                {status === 'reset-password' && (
                    <>
                        {iconBox('solar:lock-password-bold', 'primary.main', 'primary.lighter')}
                        <Typography variant="h4" fontWeight={700} gutterBottom>
                            Välj nytt lösenord
                        </Typography>
                        {resetEmail && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                              för kontot <strong>{resetEmail}</strong>
                          </Typography>
                        )}

                        <Form methods={methods} onSubmit={onResetPasswordSubmit}>
                            <Box sx={{ gap: 2.5, display: 'flex', flexDirection: 'column', textAlign: 'left', mb: 3 }}>
                                <Field.Text
                                    name="password"
                                    label="Nytt lösenord"
                                    type={showPassword ? 'text' : 'password'}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                        input: {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                        <Iconify icon={showPassword ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />

                                <Field.Text
                                    name="confirmPassword"
                                    label="Bekräfta nytt lösenord"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                        input: {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                                        <Iconify icon={showConfirmPassword ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                            </Box>

                            <Button
                                fullWidth
                                size="large"
                                type="submit"
                                variant="contained"
                                loading={isSubmitting}
                                loadingIndicator="Sparar lösenord..."
                            >
                                Spara nytt lösenord
                            </Button>
                        </Form>
                    </>
                )}

                {status === 'reset-success' && (
                    <>
                        {iconBox('solar:check-circle-bold', 'success.main', 'success.lighter')}
                        <Typography variant="h4" fontWeight={700} gutterBottom>
                            Lösenordet uppdaterat! 🎉
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                            Ditt lösenord har ändrats. Du kan nu logga in med ditt nya lösenord.
                        </Typography>
                        <Button
                            fullWidth
                            size="large"
                            variant="contained"
                            onClick={() => router.push(paths.LOGGA_IN)}
                        >
                            Logga in
                        </Button>
                    </>
                )}

                {status === 'success' && (
                    <>
                        {iconBox('solar:check-circle-bold', 'success.main', 'success.lighter')}
                        <Typography variant="h4" fontWeight={700} gutterBottom>
                            Email verifierat! 🎉
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                            Ditt konto är nu aktiverat. Du skickas vidare strax...
                        </Typography>
                        <Button
                            fullWidth
                            size="large"
                            variant="contained"
                            onClick={() => router.push(paths.LOGGA_IN)}
                        >
                            Logga in
                        </Button>
                    </>
                )}

                {status === 'already-verified' && (
                    <>
                        {iconBox('solar:info-circle-bold', 'info.main', 'info.lighter')}
                        <Typography variant="h4" fontWeight={700} gutterBottom>
                            Länken är redan använd
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                            Din email är redan verifierad. Du skickas vidare strax...
                        </Typography>
                        <Button
                            fullWidth
                            size="large"
                            variant="contained"
                            onClick={() => router.push(paths.LOGGA_IN)}
                        >
                            Logga in
                        </Button>
                    </>
                )}

                {status === 'error' && (
                    <>
                        {iconBox('solar:close-circle-bold', 'error.main', 'error.lighter')}
                        <Typography variant="h4" fontWeight={700} gutterBottom>
                            {mode === 'resetPassword'
                                ? 'Återställning misslyckades'
                                : mode === 'verifyEmail'
                                ? 'Verifiering misslyckades'
                                : 'Något gick fel'}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                            {errorMessage || 'Länken är ogiltig eller har gått ut.'}
                        </Typography>

                        <Box sx={{ gap: 2, display: 'flex', flexDirection: 'column', mt: 4 }}>
                            {mode === 'resetPassword' && (
                                <Button fullWidth size="large" variant="contained" onClick={() => router.push(paths.ÅTERSTÄLL)}>
                                    Begär ny återställningslänk
                                </Button>
                            )}
                            {mode === 'verifyEmail' && (
                                <Button fullWidth size="large" variant="contained" onClick={handleResend}>
                                    Skicka nytt verifieringsmail
                                </Button>
                            )}
                            {!mode && (
                                <Button fullWidth size="large" variant="contained" onClick={() => router.push(paths.ÅTERSTÄLL)}>
                                    Begär återställningslänk
                                </Button>
                            )}
                            <Button fullWidth size="large" variant="outlined" onClick={() => router.push(paths.LOGGA_IN)}>
                                Gå till inloggning
                            </Button>
                        </Box>
                    </>
                )}
            </Card>
        </Container>
    );
}
